import { memo, useEffect, useMemo, useRef } from "react";
import { Lock } from "lucide-react";

/**
 * Rich-text-ish editor over markdown, for the non-technical reviewer.
 *
 * - Parses the source into blocks (split on blank lines).
 * - Headings, links, images, lists, and blockquotes render as LOCKED
 *   read-only cards. She can see them but cannot edit them.
 * - Plain paragraphs render as contentEditable regions where bold
 *   (Cmd/Ctrl+B) works normally and Enter adds a paragraph break.
 *   Markdown is maintained underneath.
 * - No markdown syntax typing required.
 */

type BlockKind = "editable" | "locked";
type Block = {
  id: number;
  kind: BlockKind;
  /** Original markdown for this block (never mutated). */
  original: string;
  /** Current markdown for this block. */
  current: string;
  /** Reason a block is locked, shown as a small label. */
  lockReason?: string;
};

export function parseBlocks(md: string): Block[] {
  const raw = (md || "").replace(/\r\n/g, "\n").split(/\n{2,}/);
  const out: Block[] = [];
  raw.forEach((chunk, i) => {
    const text = chunk.replace(/\s+$/g, "");
    if (!text.trim()) return;
    const first = text.split("\n")[0] || "";
    let kind: BlockKind = "editable";
    let reason: string | undefined;
    if (/^#{1,6}\s/.test(first)) {
      kind = "locked";
      reason = "Heading";
    } else if (/^\s*>/.test(first)) {
      kind = "locked";
      reason = "Quote";
    } else if (/^\s*(?:[-*+]\s|\d+\.\s)/.test(first)) {
      kind = "locked";
      reason = "List";
    } else if (/^\s*(?:\||\|-)/.test(first)) {
      kind = "locked";
      reason = "Table";
    } else if (/^\s*```/.test(first)) {
      kind = "locked";
      reason = "Code";
    } else if (/!\[[^\]]*\]\([^)]+\)/.test(text)) {
      kind = "locked";
      reason = "Image";
    } else if (/\[[^\]]+\]\([^)]+\)/.test(text) || /<https?:\/\//i.test(text) || /https?:\/\/\S/i.test(text)) {
      kind = "locked";
      reason = "Contains a link";
    }
    out.push({ id: i, kind, original: text, current: text, lockReason: reason });
  });
  return out;
}

export function serializeBlocks(blocks: Block[]): string {
  return blocks.map((b) => b.current).join("\n\n");
}

function escapeHtml(s: string) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Convert a plain-paragraph markdown block to safe HTML for contentEditable. */
function editableMdToHtml(md: string): string {
  const paras = md.split(/\n{2,}/);
  return paras
    .map((p) => {
      const esc = escapeHtml(p).replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>").replace(/\n/g, "<br>");
      return `<p>${esc || "<br>"}</p>`;
    })
    .join("");
}

/** Convert a contentEditable DOM back to markdown (bold + paragraph breaks only). */
function editableHtmlToMd(root: HTMLElement): string {
  const walkInline = (nodes: ArrayLike<ChildNode>): string => {
    let out = "";
    Array.from(nodes).forEach((n) => {
      if (n.nodeType === 3) {
        out += (n.textContent || "").replace(/\u00A0/g, " ");
      } else if (n.nodeType === 1) {
        const el = n as HTMLElement;
        const tag = el.tagName.toLowerCase();
        if (tag === "br") out += "\n";
        else if (tag === "strong" || tag === "b") {
          const inner = walkInline(el.childNodes).trim();
          out += inner ? `**${inner}**` : "";
        } else if (tag === "p" || tag === "div") {
          // handled at block level
          out += walkInline(el.childNodes);
        } else {
          out += walkInline(el.childNodes);
        }
      }
    });
    return out;
  };

  const kids = Array.from(root.childNodes);
  const isBlockEl = (n: ChildNode) =>
    n.nodeType === 1 && ["p", "div"].includes((n as HTMLElement).tagName.toLowerCase());
  const hasBlocks = kids.some(isBlockEl);
  if (!hasBlocks) {
    return walkInline(root.childNodes).replace(/[ \t]+\n/g, "\n").trim();
  }
  const paras: string[] = [];
  kids.forEach((k) => {
    if (isBlockEl(k)) {
      const t = walkInline(k.childNodes).replace(/[ \t]+\n/g, "\n").trim();
      paras.push(t);
    } else if (k.nodeType === 3) {
      const t = (k.textContent || "").replace(/\u00A0/g, " ").trim();
      if (t) paras.push(t);
    }
  });
  // preserve intentionally empty paragraphs? No — collapse consecutive blanks
  return paras.filter((_, i, a) => a[i] !== "" || (i > 0 && a[i - 1] !== "")).join("\n\n").trim();
}

const EditableBlock = memo(function EditableBlock({
  block,
  onChange,
}: {
  block: Block;
  onChange: (md: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const hydratedRef = useRef(false);
  // Hydrate innerHTML exactly once on mount. After that, the browser (and
  // browser extensions like Grammarly / password managers) owns the DOM
  // inside this contentEditable region. We never let React reconcile it,
  // which is what caused "removeChild: The node to be removed is not a
  // child of this node" crashes on /review.
  useEffect(() => {
    if (hydratedRef.current) return;
    if (ref.current) {
      ref.current.innerHTML = editableMdToHtml(block.current);
      hydratedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      // Empty React children + an initial innerHTML hydration means React has
      // no virtual children to reconcile against, so extension-injected nodes
      // cannot desync the tree.
      onInput={() => {
        if (!ref.current) return;
        onChange(editableHtmlToMd(ref.current));
      }}
      onPaste={(e) => {
        // Paste as plain text to prevent smuggling in headings, links, styles.
        e.preventDefault();
        const text = e.clipboardData.getData("text/plain");
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        sel.deleteFromDocument();
        sel.getRangeAt(0).insertNode(document.createTextNode(text));
        sel.collapseToEnd();
        if (ref.current) onChange(editableHtmlToMd(ref.current));
      }}
      className="prose prose-sm max-w-none text-sm leading-relaxed outline-none rounded-md border border-input bg-background px-3 py-2 min-h-[3.5rem] focus:ring-2 focus:ring-ring focus:border-ring"
    />
  );
}, (prev, next) => prev.block.id === next.block.id);

function LockedBlock({ block }: { block: Block }) {
  return (
    <div className="rounded-md border border-dashed border-muted-foreground/30 bg-muted/30 px-3 py-2 text-xs">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
        <Lock className="h-3 w-3" /> {block.lockReason || "Locked"} (read only)
      </div>
      <pre className="whitespace-pre-wrap font-serif text-sm text-foreground/80 m-0">{block.original}</pre>
    </div>
  );
}

export function MarkdownBlockEditor({
  value,
  originalMarkdown,
  onChange,
}: {
  value: string;
  originalMarkdown: string;
  onChange: (md: string) => void;
}) {
  // Block structure is derived from the ORIGINAL, so headings/links stay pinned
  // as locked slots. Editable slots are hydrated from the current `value` by
  // matching them in order to the editable slots in the original.
  const structure = useMemo(() => parseBlocks(originalMarkdown), [originalMarkdown]);
  const currentBlocks = useMemo(() => parseBlocks(value), [value]);

  const blocks: Block[] = useMemo(() => {
    let editableIdx = 0;
    const currentEditables = currentBlocks.filter((b) => b.kind === "editable");
    return structure.map((s) => {
      if (s.kind === "locked") return s;
      const match = currentEditables[editableIdx++];
      return { ...s, current: match ? match.current : s.original };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structure, value]);

  const emit = (idx: number, md: string) => {
    const next = blocks.map((b, i) => (i === idx ? { ...b, current: md } : b));
    onChange(serializeBlocks(next));
  };

  return (
    <div className="space-y-2 min-h-[55vh]">
      {blocks.map((b, i) =>
        b.kind === "locked" ? (
          <LockedBlock key={`lock-${b.id}`} block={b} />
        ) : (
          <EditableBlock key={`edit-${b.id}`} block={b} onChange={(md) => emit(i, md)} />
        )
      )}
    </div>
  );
}

/** Original-pane view with a "changed" marker next to each block. */
export function OriginalWithChangeMarkers({
  originalMarkdown,
  proposedMarkdown,
}: {
  originalMarkdown: string;
  proposedMarkdown: string;
}) {
  const original = useMemo(() => parseBlocks(originalMarkdown), [originalMarkdown]);
  const proposed = useMemo(() => parseBlocks(proposedMarkdown), [proposedMarkdown]);
  // Align by editable-index within each kind stream so locked blocks aren't
  // spuriously flagged and edits to editable blocks match up positionally.
  const proposedByKindIndex: Record<BlockKind, string[]> = {
    editable: proposed.filter((b) => b.kind === "editable").map((b) => b.current),
    locked: proposed.filter((b) => b.kind === "locked").map((b) => b.current),
  };
  let ei = 0;
  let li = 0;
  return (
    <div className="space-y-2 min-h-[55vh]">
      {original.map((b) => {
        const counterpart =
          b.kind === "editable" ? proposedByKindIndex.editable[ei++] : proposedByKindIndex.locked[li++];
        const changed = (counterpart ?? "").trim() !== b.original.trim();
        return (
          <div key={b.id} className="flex items-start gap-2">
            <div
              className={`mt-2 h-2 w-2 rounded-full shrink-0 ${
                changed ? "bg-amber-500" : "bg-transparent"
              }`}
              title={changed ? "You changed this paragraph" : ""}
              aria-label={changed ? "Changed" : "Unchanged"}
            />
            <div
              className={`flex-1 rounded-md px-3 py-2 text-sm font-serif whitespace-pre-wrap ${
                b.kind === "locked" ? "bg-muted/40 text-foreground/80" : "bg-background"
              } ${changed ? "border border-amber-400/60" : "border border-transparent"}`}
            >
              {b.original}
            </div>
          </div>
        );
      })}
    </div>
  );
}
